/**
 * Mobile Filter Modal Component
 * Stage 9: Authentication Links & Mobile Polish
 */

import React, { useState, useEffect, useRef } from 'react';
import { FilterState, StyleState } from '../types/api';
import { ASSETS } from '../assets/assetMapping';
import { stringToHairArray, formatHairDisplay } from '../utils/filterUtils';

interface MobileFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

const styleOptions = {
  type: [
    { value: 'photorealistic', label: 'Photorealistic', image: ASSETS.PHOTOREALISTIC_STYLE },
    { value: 'cartoon', label: 'Cartoon', image: ASSETS.CARTOON_STYLE }
  ],
  ratio: [
    { value: 'vertical', label: 'Vertical', image: ASSETS.VERTICAL_RATIO },
    { value: 'horizontal', label: 'Horizontal', image: ASSETS.VERTICAL_RATIO } 
  ],
  scene: [
    { value: 'headshot', label: 'Headshot', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 4, focusTop: 'head' },
    { value: 'mid-closeup', label: 'Mid close-up', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 2, focusTop: 'mid' },
    { value: 'full-body', label: 'Full body', image: ASSETS.FULL_BODY_SCENE, zoomLevel: 1, focusTop: 'full' }
  ]
};

const filterOptions = {
  age: [
    { value: 'under20', label: '<20' },
    { value: '20s', label: '20s' },
    { value: '30s', label: '30s' },
    { value: '40s', label: '40s' },
    { value: '50s', label: '50s' },
    { value: '60plus', label: '60+' },
    { value: 'any', label: 'Any' }
  ],
  gender: [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'any', label: 'Any' }
  ],
  hair: [
    { value: 'blonde', label: 'Blonde' },
    { value: 'brunette', label: 'Brunette' },
    { value: 'black', label: 'Black hair' },
    { value: 'curly', label: 'Curly' },
    { value: 'straight', label: 'Straight' },
    { value: 'grey', label: 'Grey' },
    { value: 'pink', label: 'Pink' },
    { value: 'any', label: 'Any' }
  ],
  background: [
    { value: 'Green Screen', label: 'Green Screen' },
    { value: 'Office Desk', label: 'Office Desk' },
    { value: 'Interview setup with mic', label: 'Interview setup with mic' },
    { value: 'Car interior', label: 'Car interior' },
    { value: 'Holding laptop', label: 'Holding laptop' },
    { value: 'Coffee shop', label: 'Coffee shop' },
    { value: 'Home Office', label: 'Home Office' },
    { value: 'any', label: 'Any' }
  ],
  model: [
    { value: 'flux-dev', label: 'FLUX Dev' },
    { value: 'seedream-4', label: 'Seedream 4.0' },
    { value: 'nano-banana', label: 'Nano Banana' }
  ]
};

export default function MobileFilterModal({ 
  isOpen, 
  onClose, 
  filters, 
  onFiltersChange 
}: MobileFilterModalProps) {
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);

  // Update local filters when props change
  useEffect(() => {
    const convertedFilters = {
      ...filters,
      hair: Array.isArray(filters.hair) ? filters.hair : stringToHairArray(filters.hair as any)
    };
    setLocalFilters(convertedFilters);
  }, [filters]);

  // Lock/unlock body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Unlock body scroll when modal closes
        const bodyTop = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        // Restore scroll position
        window.scrollTo(0, parseInt(bodyTop || '0') * -1);
      };
    }
  }, [isOpen]);

  // Reset translateY when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTranslateY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Handle touch start
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    
    // Only allow downward swipe
    if (deltaY > 0) {
      setTranslateY(deltaY);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    setIsDragging(false);
    
    // Threshold: if swiped down more than 100px, close the modal
    const threshold = 100;
    if (translateY > threshold) {
      onClose();
    } else {
      // Snap back to original position
      setTranslateY(0);
    }
  };

  // Prevent backdrop interaction
  const handleBackdropTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle filter change
  const handleFilterChange = (category: keyof FilterState, value: string | string[]) => {
    const newFilters = { ...localFilters, [category]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Handle style change
  const handleStyleChange = (category: keyof StyleState, value: string) => {
    const newStyle = { ...localFilters.style, [category]: value as any };
    const newFilters = { ...localFilters, style: newStyle };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Close modal and apply filters
  // const handleApply = () => {
  //   onFiltersChange(localFilters);
  //   onClose();
  // };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-end justify-center z-[99999] lg:hidden"
      onClick={handleBackdropClick}
      onTouchStart={handleBackdropTouchStart}
      style={{ touchAction: 'none' }}
    >
      <div 
        ref={modalRef}
        className={`bg-white w-full max-h-[90vh] rounded-t-[15px] overflow-hidden ${
          !isDragging ? 'animate-slide-up' : ''
        }`}
        style={{ 
          transform: `translateY(${translateY}px)`,
          touchAction: 'pan-y'
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle Bar */}
        <div 
          className="flex items-center justify-center py-2.5 px-[174px] cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
        >
          <div className="bg-[rgba(37,54,88,0.15)] h-1 w-[26px] rounded-[3px]" />
        </div>

        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="font-['Inter'] font-bold text-[18px] text-[#253658]">
              Appearance
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-5 pt-0 max-h-[70vh] overflow-y-auto">
          <div className="space-y-5">
            {/* Style Section - Type */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658] tracking-[0.14px]">
                Type
              </h3>
              <div className="flex flex-wrap gap-2">
                {styleOptions.type.map((option) => (
                  <div key={option.value} className="flex flex-col items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStyleChange('type', option.value);
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`w-24 h-24 rounded-[10px] relative overflow-hidden transition-all ${
                        localFilters.style.type === option.value
                          ? 'bg-[rgba(68,129,253,0.08)] border-2 border-[#4481fd]'
                          : 'bg-[rgba(68,129,253,0.08)] hover:bg-[rgba(68,129,253,0.12)]'
                      }`}
                    >
                      <div className="absolute inset-0 overflow-hidden rounded-[10px]">
                        <img 
                          src={option.image} 
                          alt={option.label}
                          className={`absolute left-[-10.43%] max-w-none w-[117.96%] h-[117.96%] top-[-7.55%] ${
                            // Add specific styling for cartoon images to prevent smearing
                            option.value === 'cartoon' ? 'object-cover object-center' : ''
                          }`}
                        />
                      </div>
                      {localFilters.style.type === option.value && (
                        <div className="absolute border-[#4481fd] border-[4px] border-solid inset-[-3px] rounded-[13px]" />
                      )}
                    </button>
                    <span className="font-['Inter'] font-normal text-[12px] text-[#253658] text-center">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ratio Section */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658] tracking-[0.14px]">
                Ratio
              </h3>
              <div className="flex flex-wrap gap-2">
                {styleOptions.ratio.map((option) => (
                  <div key={option.value} className="flex flex-col items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStyleChange('ratio', option.value);
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`w-24 h-24 rounded-[10px] relative overflow-hidden transition-all ${
                        localFilters.style.ratio === option.value
                          ? 'bg-[rgba(68,129,253,0.08)] border-2 border-[#4481fd]'
                          : 'bg-[rgba(68,129,253,0.08)] hover:bg-[rgba(68,129,253,0.12)]'
                      }`}
                    >
                      <div className="absolute inset-0 overflow-hidden rounded-[10px]">
                        <img 
                          src={option.image} 
                          alt={option.label}
                          className={`absolute left-[-10.43%] max-w-none w-[117.96%] h-[117.96%] top-[-7.55%] ${option.value === 'horizontal' ? 'transform -rotate-90' : ''}`}
                        />
                      </div>
                      {localFilters.style.ratio === option.value && (
                        <div className="absolute border-[#4481fd] border-[4px] border-solid inset-[-3px] rounded-[13px]" />
                      )}
                    </button>
                    <span className="font-['Inter'] font-normal text-[12px] text-[#253658] text-center">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Scene Section */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658] tracking-[0.14px]">
                Scene
              </h3>
              <div className="flex flex-wrap gap-2">
                {styleOptions.scene.map((option) => (
                  <div key={option.value} className="flex flex-col items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStyleChange('scene', option.value);
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`w-24 h-24 rounded-[10px] relative overflow-hidden transition-all ${
                        localFilters.style.scene === option.value
                          ? 'bg-[rgba(68,129,253,0.08)] border-2 border-[#4481fd]'
                          : 'bg-[rgba(68,129,253,0.08)] hover:bg-[rgba(68,129,253,0.12)]'
                      }`}
                    >
                      <div className="absolute inset-0 overflow-hidden rounded-[10px]">
                        <img 
                          src={option.image} 
                          alt={option.label}
                          className={`absolute left-[-10.43%] max-w-none w-[117.96%] h-[117.96%] ${
                            option.focusTop === 'head' ? 'top-[130%]' : 
                            option.focusTop === 'mid' ? 'top-[40%]' : 'top-[-7.55%]'
                          }`}
                          style={{ 
                            transform: `scale(${option.zoomLevel || 1})`
                          }}
                        />
                      </div>
                      {localFilters.style.scene === option.value && (
                        <div className="absolute border-[#4481fd] border-[4px] border-solid inset-[-3px] rounded-[13px]" />
                      )}
                    </button>
                    <span className="font-['Inter'] font-normal text-[12px] text-[#253658] text-center">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Age Section */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658]">
                Age
              </h3>
              <div className="flex flex-wrap gap-2">
                {filterOptions.age.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterChange('age', option.value);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`px-3 py-1.5 rounded-full border transition-all ${
                      localFilters.age === option.value
                        ? 'bg-[#253658] text-white border-[#253658]'
                        : 'bg-white text-[#253658] border-[rgba(37,54,88,0.3)] hover:border-[#253658]'
                    }`}
                  >
                    <span className="font-['Inter'] font-medium text-[14px] leading-[1.4]">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Gender Section */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658]">
                Gender
              </h3>
              <div className="flex flex-wrap gap-2">
                {filterOptions.gender.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterChange('gender', option.value);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`px-3 py-1.5 rounded-full border transition-all ${
                      localFilters.gender === option.value
                        ? 'bg-[#253658] text-white border-[#253658]'
                        : 'bg-white text-[#253658] border-[rgba(37,54,88,0.3)] hover:border-[#253658]'
                    }`}
                  >
                    <span className="font-['Inter'] font-medium text-[14px] leading-[1.4]">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Hair Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658]">
                  Hair
                </h3>
                <span className="font-['Inter'] font-medium text-[12px] text-[#253658] opacity-60">
                  {formatHairDisplay(localFilters.hair)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {filterOptions.hair.filter(option => option.value !== 'any').map((option) => {
                  const isSelected = Array.isArray(localFilters.hair) 
                    ? localFilters.hair.includes(option.value)
                    : localFilters.hair === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        const currentHair = Array.isArray(localFilters.hair) 
                          ? localFilters.hair 
                          : stringToHairArray(localFilters.hair as any);
                        const newHair = isSelected
                          ? currentHair.filter(v => v !== option.value)
                          : [...currentHair, option.value];
                        handleFilterChange('hair', newHair);
                      }}
                      onTouchStart={(e) => e.stopPropagation()}
                      className={`px-3 py-1.5 rounded-full border transition-all ${
                        isSelected
                          ? 'bg-[#253658] text-white border-[#253658]'
                          : 'bg-white text-[#253658] border-[rgba(37,54,88,0.3)] hover:border-[#253658]'
                      }`}
                    >
                      <span className="font-['Inter'] font-medium text-[14px] leading-[1.4]">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Background Section */}
            <div className="space-y-2">
              <h3 className="font-['Inter'] font-bold text-[14px] text-[#253658]">
                Background
              </h3>
              <div className="flex flex-wrap gap-2">
                {filterOptions.background.map((option) => (
                  <button
                    key={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFilterChange('background', option.value);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`px-3 py-1.5 rounded-full border transition-all ${
                      localFilters.background === option.value
                        ? 'bg-[#253658] text-white border-[#253658]'
                        : 'bg-white text-[#253658] border-[rgba(37,54,88,0.3)] hover:border-[#253658]'
                    }`}
                  >
                    <span className="font-['Inter'] font-medium text-[14px] leading-[1.4]">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Home Indicator (iOS style) */}
        <div className="h-[34px] relative flex items-center justify-center">
          <div className="bg-black h-[5px] w-[134px] rounded-full opacity-30" />
        </div>
      </div>
    </div>
  );
}

// Add slide-up animation to tailwind
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;
document.head.appendChild(style);
